import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import toast from 'react-hot-toast';
import TableComponent from 'components/table-component-new/Table';
import TablePagination from 'components/table-component-new/TablePagination';
import { activeTasksHeaderList } from '../utils';
import { CallAPI } from 'actions/General';
import TaskListHeader from './TaskListHeader';
import TaskSubmissions from './task-submissions/TaskSubmissions';
import TaskDetailDrawer from './TaskDetailDrawer';

const TaskList = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [displayEntries, setDisplayEntries] = useState(10);
  const [selectedRows, setSelectedRows] = useState([]);
  const [taskList, setTaskList] = useState([]);
  const [taskDetail, setTaskDetail] = useState(null);
  const [submissionsTaskId, setSubmissionsTaskId] = useState(null);
  const [companyFieldOptions, setCompanyFieldOptions] = useState([]);
  const [taskLabelsOptions, setTaskLabelsOptions] = useState([]);
  const [filter, setFilter] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [companyFieldIdRowIdToDisplayValueMap, setCompanyFieldIdRowIdToDisplayValueMap] =
    useState(null);

  const handleDebouncedFilter = (name, value) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const clearListing = () => {
    setSelectedRows([]);
    setTaskList([]);
    setTotalRecords(0);
  };

  const fetchTaskList = async (page = 1, limit = displayEntries) => {
    clearListing();
    const payload = { state: 'active', page, limit };
    if (filter.search) payload.searchText = filter.search;
    if (filter.status?.length) payload.status = filter.status;
    if (filter.priority?.length) payload.priority = filter.priority;
    if (filter.sortBy) payload.sortBy = filter.sortBy;
    if (filter.labels?.length) payload.labels = filter.labels;
    if (filter.companyFieldValues?.length && companyFieldIdRowIdToDisplayValueMap) {
      payload.companyFieldValues = filter.companyFieldValues.map(
        (v) => companyFieldIdRowIdToDisplayValueMap[v],
      );
    }

    // DUE DATE
    if (filter.dueDate?.type && filter.dueDate?.type !== 'all') {
      payload.dueDate = filter.dueDate.type;
      if (filter.dueDate?.type === 'custom') {
        const range = filter.dueDate.range;
        payload.dueFromDate = range[0];
        payload.dueToDate = range[1];
      }
    }

    // COMPLETION DATE
    if (filter.completionDate?.type && filter.completionDate?.type !== 'all') {
      payload.completionDate = filter.completionDate.type;
      if (filter.completionDate?.type === 'custom') {
        const range = filter.completionDate.range;
        payload.completionFromDate = range[0];
        payload.completionToDate = range[1];
      }
    }

    const result = await CallAPI('GET_ALL_TASKS', payload, null, setLoading);
    if (result.status) {
      result.data.list.forEach((t) => {
        t.displayRefNumber = `TK-${t.referenceNumber}`;
        t.formTitle = t.form?.title ?? '';
        t.companyFieldName = t.companyField?.fieldTitle ?? '';
      });
      setTaskList(result.data.list);
      setTotalRecords(result.data.count);
    }
  };

  const fetchCompanyFieldList = async () => {
    const { status, data } = await CallAPI('GET_ALL_COMPANY_FIELDS', {});
    if (!status || !data?.length) return;

    const fieldMap = {};
    // unique objects in payload
    const seen = {};
    const payload = { items: [] };

    for (const item of data) {
      fieldMap[item._id] = {
        label: item.fieldTitle,
        dataSourceId: item.dataSource._id,
        dataSourceColumn: item.dataSourceColumn,
      };

      const key = `${item.dataSource._id}_${item.dataSourceColumn}`;
      if (seen[key]) continue;
      seen[key] = true;

      payload.items.push({
        dataSourceId: item.dataSource._id,
        columnId: item.dataSourceColumn,
      });
    }

    const entriesResponse = await CallAPI('GET_MULTIPLE_DATASOURCE_ENTRIES', payload);
    if (entriesResponse.status) {
      const options = [];
      const companyFieldMap = {};
      for (const fieldId in fieldMap) {
        const { dataSourceId, label, dataSourceColumn } = fieldMap[fieldId];
        const ds = entriesResponse.data.find((d) => d.dataSourceId === dataSourceId);
        const entries = (ds?.entries || [])
          .filter((e) => e.entryValue?.keyValue && e.entryValue.columnId === dataSourceColumn)
          .map((e) => {
            const key = `${fieldId}_${e.rowId}`;
            const displayValue = e.entryValue.keyValue;
            companyFieldMap[key] = displayValue;
            return { value: key, displayValue: displayValue };
          });

        options.push({
          value: fieldId,
          displayValue: label,
          selectMultiple: true,
          children: entries,
        });
      }
      setCompanyFieldOptions(options);
      setCompanyFieldIdRowIdToDisplayValueMap(companyFieldMap);
    }
  };

  const fetchTaskLabelList = async () => {
    const result = await CallAPI('GET_ALL_TASK_LABELS');
    if (result.status) {
      setTaskLabelsOptions(
        (result?.data ?? []).map(({ _id, name }) => ({
          value: _id,
          displayValue: name,
        })),
      );
    }
  };

  const openChangeAssigneeCallback = async (task, data) => {
    const payload = { assignedUsers: data.users, assignedGroups: data.groups };

    const result = await CallAPI('UPDATE_TASK_ASSIGNEES', payload, task._id);
    if (result.status) {
      toast.success('Task assignees updated successfully');
      fetchTaskList();
    }
  };

  const actionsCallBack = async (action, data) => {
    if (action === 'archive') {
      const result = await CallAPI('UPDATE_TASKS_STATE', { taskIds: data.ids, state: 'archived' });
      if (result.status) {
        fetchTaskList();
      } else {
        toast.error(result.error);
      }
    } else if (action === 'delete' || action === 'close') {
      const payload = { taskIds: data.ids };
      const apiMap = {
        close: 'CLOSE_BULK_TASKS',
        delete: 'DELETE_BULK_TASKS',
      };
      const result = await CallAPI(apiMap[action], payload);
      if (result.status) {
        fetchTaskList();
      } else {
        toast.error(result.error);
      }
    }
  };

  const onRowsSelection = (name, value) => {
    let tempSelection = [];
    const mappedObject = (obj) => ({ id: obj._id, startDate: obj.startDate, status: obj.status });

    if (name === 'all') {
      if (value) tempSelection = taskList.map((t) => mappedObject(t));
      else tempSelection = [];
    } else {
      tempSelection = cloneDeep(selectedRows);

      if (value) {
        const selectedTask = taskList.find((t) => t._id === name);

        if (selectedTask) tempSelection.push(mappedObject(selectedTask));
      } else tempSelection = tempSelection.filter((s) => s.id !== name);
    }

    setSelectedRows(tempSelection);
  };

  const openTaskSubmissionsCallback = (id) => {
    setSubmissionsTaskId(id);
  };

  const openTaskDetailDrawer = (row) => {
    setTaskDetail(row);
  };

  const fetchAllUsersGroups = async () => {
    const result = await CallAPI('GET_SCHEDULE_OPTIONS_LIST');
    if (result.status) {
      setAllUsers(result.data.usersList);
      setAllGroups(result.data.groupsList);
    }
  };

  useEffect(() => {
    fetchTaskLabelList();
    fetchCompanyFieldList();
    fetchAllUsersGroups();
  }, []);

  useEffect(() => {
    fetchTaskList();
  }, [filter]);

  return (
    <div>
      <div className='d-flex gap-1 flex-column'>
        <label className='theme-size-2 theme-text-black-1'>{t('text_active_tasks')}</label>
        <label>{t('text_active_tasks_description')}</label>
      </div>
      <TaskListHeader
        filter={filter}
        setFilter={setFilter}
        handleDebouncedFilter={handleDebouncedFilter}
        taskLabelsOptions={taskLabelsOptions}
        companyFieldOptions={companyFieldOptions}
        selectedRows={selectedRows}
        actionsCallBack={actionsCallBack}
      />
      <TableComponent
        header={activeTasksHeaderList}
        data={taskList}
        props={{
          translateValueFunc: (val) => t(val),
          openTaskSubmissionsCallback,
          openTaskDetailDrawer,
          openChangeAssigneeCallback,
        }}
        totalRecords={totalRecords}
        loader={loading}
        isFieldSelectionEnabled={true}
        isActionsEnabled={false}
        isSelectionEnabled={true}
        selectionKey={'_id'}
        onSelection={onRowsSelection}
        selectedRows={selectedRows}
        headerCellBorder='vertical'
        bodyCellBorder='both'
        noWrap
      />

      {totalRecords > 0 && (
        <TablePagination
          totalRecords={totalRecords}
          currentPageRecords={taskList.length}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          displayEntries={displayEntries}
          setDisplayEntries={setDisplayEntries}
          listingFunction={fetchTaskList}
        />
      )}
      {submissionsTaskId && (
        <TaskSubmissions taskId={submissionsTaskId} closeModal={() => setSubmissionsTaskId(null)} />
      )}

      {taskDetail && (
        <TaskDetailDrawer
          taskDetail={taskDetail}
          handleClose={() => setTaskDetail(null)}
          allUsers={allUsers}
          allGroups={allGroups}
        />
      )}
    </div>
  );
};

export default TaskList;
